// Problem Reporter — console + error ring buffer.
//
// Imported at the VERY TOP of main.tsx (before App) so it captures from app start. Patches
// console.log/info/warn/error (always calling through to the originals — nothing is swallowed)
// and listens for window.onerror + unhandledrejection. The buffer is a bounded ring (~100
// entries); each message is safely stringified (circular-ref-safe) and truncated, so a noisy
// app can never grow this without bound. getConsoleTail() snapshots it for a problem report.
//
// Deliberately tiny and dependency-free; this module must not import app code (it loads first).

export interface ConsoleEntry {
  t: string;     // ISO timestamp
  level: 'log' | 'info' | 'warn' | 'error';
  msg: string;
}

const MAX_ENTRIES = 100;
const MAX_MSG_CHARS = 500;

const buffer: ConsoleEntry[] = [];

// Circular-ref-safe stringify of one console argument.
function safeArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  if (typeof arg !== 'object') return String(arg);
  try {
    const seen = new WeakSet<object>();
    return JSON.stringify(arg, (_k, v) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v as object)) return '[Circular]';
        seen.add(v as object);
      }
      return v as unknown;
    });
  } catch {
    try { return String(arg); } catch { return '[unstringifiable]'; }
  }
}

function push(level: ConsoleEntry['level'], args: unknown[]): void {
  let msg = args.map(safeArg).join(' ');
  if (msg.length > MAX_MSG_CHARS) msg = msg.slice(0, MAX_MSG_CHARS) + '…';
  buffer.push({ t: new Date().toISOString(), level, msg });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

let installed = false;

function install(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  (['log', 'info', 'warn', 'error'] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]): void => {
      try { push(level, args); } catch { /* never let capture break logging */ }
      original(...args);
    };
  });

  window.addEventListener('error', (e: ErrorEvent) => {
    try {
      const where = e.filename ? ` (${e.filename}:${e.lineno ?? '?'}:${e.colno ?? '?'})` : '';
      push('error', [`[window.onerror] ${e.message ?? 'Unknown error'}${where}`]);
    } catch { /* ignore */ }
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    try {
      const reason = e.reason instanceof Error ? `${e.reason.name}: ${e.reason.message}` : safeArg(e.reason);
      push('error', [`[unhandledrejection] ${reason}`]);
    } catch { /* ignore */ }
  });
}

install();

/** Snapshot of the recent console/error ring buffer for a problem report. */
export function getConsoleTail(): ConsoleEntry[] {
  return buffer.slice();
}
